import { deleteRequest, getRequest, postRequest, putRequest } from '.'
import Difficulty from 'app/frontend/types/custom/Difficulty'

export const getCharacterDetails = async (
  ids: number[],
): Promise<wowaudit.CharacterDetails[]> => {
  return await getRequest(
    `/api/characters/details?ids[]=${ids.join('&ids[]=')}`,
  )
}

export const getCharacter = async (id: number): Promise<wowaudit.Character> => {
  return await getRequest(`/api/characters/${id}`)
}

export const getCharacterByPath = async (
  path: string,
): Promise<wowaudit.Character> => {
  return await getRequest(
    `/api/characters/search?path=${encodeURIComponent(path)}`,
  )
}

export type CharacterKeystones = Record<
  number,
  Record<string, { dungeon: number; level: number }>
>

export interface CharacterProgress {
  keystones: CharacterKeystones
  raidProgression: {
    [key in Difficulty]: {
      [id: string]: { kills: number; parse: number | null }
    }
  }
  weeklyRaidProgression: {
    [key in Difficulty]: { [id: string]: boolean }
  }
  alts: wowaudit.CharacterReference[]
  altDetails: Record<
    number,
    {
      maxItemLevel?: number
      mythicRating?: number
      mythicRatingColor?: { overall: string } & { [key: string]: string }
      currentRaidProgression?: wowaudit.CharacterDetails['currentRaidProgression']
    }
  >
}

export const getCharacterProgress = async (
  id: number,
): Promise<CharacterProgress> => {
  return await getRequest(`/api/characters/${id}/progress`)
}

export const updateCharacter = async (
  id: number,
  params?: Record<string, string>,
): Promise<{ job_id: string }> => {
  return await putRequest(`/api/characters/${id}`, params)
}

export const transferCharacter = async (
  id: number,
  data: { realm: wowaudit.Realm; name: string },
): Promise<{ job_id: string }> => {
  return await postRequest(`/api/characters/${id}/transfer`, data)
}

export const uploadWishlistReport = async (
  teamId: number,
  characterId: number,
  reportId: string,
  configurationId: number,
  clearExisting: boolean,
): Promise<{ job_id: string }> => {
  return await putRequest(
    `/api/teams/${teamId}/character_wishlists/${characterId}`,
    {
      report_id: reportId,
      droptimizer_configuration_id: configurationId,
      replace_manual_edits: clearExisting,
    },
  )
}

export const manuallyUpdateWishlist = async (
  teamId: number,
  characterId: number,
  seasonId: number,
  changes: Record<string, unknown>,
): Promise<wowaudit.CharacterWishlist> => {
  return await putRequest(
    `/api/teams/${teamId}/character_wishlists/${characterId}`,
    { changes, season_id: seasonId },
  )
}

export const updateBonusRollTarget = async (
  teamId: number,
  characterId: number,
  encounterIds: number[],
): Promise<wowaudit.Team> => {
  return await putRequest(
    `/api/teams/${teamId}/character_wishlists/${characterId}/bonus_roll_target`,
    { bonusRollEncounterIds: encounterIds },
  )
}

export const updateTierPriority = async (
  teamId: number,
  characterId: number,
  seasonId: number,
  tierPriority: Record<string, string>,
): Promise<wowaudit.Team> => {
  return await putRequest(
    `/api/teams/${teamId}/character_wishlists/${characterId}/tier_priority`,
    { tierPriority, seasonId },
  )
}

export type TrinketChoice = { item_id: number; obtained: boolean } | null

export const updateTrinketPriority = async (
  teamId: number,
  characterId: number,
  seasonId: number,
  trinketPriority: Record<string, TrinketChoice>,
): Promise<wowaudit.Team> => {
  return await putRequest(
    `/api/teams/${teamId}/character_wishlists/${characterId}/trinket_priority`,
    { trinket_priority: trinketPriority, season_id: seasonId },
  )
}

export const deleteWishlistsForTeam = async (
  characterId: number,
  teamId: number,
  seasonId: number,
): Promise<{ success: boolean }> => {
  return await deleteRequest(
    `/api/teams/${teamId}/character_wishlists/${characterId}?season_id=${seasonId}`,
  )
}
